import {
  ClaimTypeHiro,
  type PaAPI,
  type PolicyClaimType,
  type WorkersApiLegacy,
} from '@rialtic/types'
import type { Api } from '@rialtic/api'
import { format, min } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ALL_LOB, datePresets, sinceInceptionDate } from '~/constants'
import { useAppConfiguration } from './appConfiguration'
import { useConnectorAnalytics } from './connectorAnalytics'

const DATE_START_KEY = 'start_date'
const DATE_END_KEY = 'end_date'
const PRESET_KEY = 'preset'

interface ConnectorOptions {
  force?: boolean
  middleware?: boolean
}
interface MuxFilters {
  connector_id: string
  start_date: string
  end_date: string
  start_iso?: string
  end_iso?: string
}

interface SelectedDateRangeWithTimezone {
  range: [string, string]
  timezone: string
}

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
}

type LocalConnector = PaAPI.Get['/connector/{id}'] & {
  isManager?: boolean
}

export enum LocalStorageKeys {
  workspaceId = '@rialtic/workspaceId',
  connectorId = '@rialtic/connectorId',
  selectedDateRange = '@rialtic/selectedDateRange',
}
export enum Role {
  Analyst = 'Analyst',
  Manager = 'Manager',
  SystemAdmin = 'SystemAdmin',
  UserAdmin = 'UserAdmin',
}

const hasConnectors = (
  workspaceId = '',
  connectorId = '',
  connectors: PaAPI.Get['/connector'] = [],
): boolean => !!(workspaceId && connectorId && connectors?.length)

export const useWorkspace = defineStore('workspace', {
  state: () => {
    const tenantConfig = useAppConfig().tenant

    return {
      connectorId: useLocalStorage(LocalStorageKeys.connectorId, ''),
      previousConnectorId: '',
      connectorRoles: {} as Record<string, boolean>,
      globalRoles: {} as Record<string, boolean>,
      selectedConnectors: new Set<string>(),
      user: null as User | null,
      userWorkspaces: {} as Record<string, PaAPI.Get['/workspace/{id}']>,
      userConnectors: [] as LocalConnector[],
      workspaceRoles: {} as Record<string, boolean>,
      workspaceId: tenantConfig.singleTenantWorkspaceId
        ? tenantConfig.singleTenantWorkspaceId
        : useLocalStorage(LocalStorageKeys.workspaceId, ''),
    }
  },

  getters: {
    activeConnector(state): LocalConnector | null {
      if (state.connectorId === ALL_LOB) return null
      return (
        this.connectors.find(({ id }) => id === state.connectorId.toString()) ||
        null
      )
    },

    activeWorkspace: (state): PaAPI.Get['/workspace/{id}'] | null => {
      const tenantConfig = useAppConfig().tenant

      const wId = tenantConfig.singleTenantWorkspaceId || state.workspaceId
      return wId ? state.userWorkspaces[wId] : null
    },

    availableConnectors(): LocalConnector[] {
      return this.connectors.filter(
        ({ system_document }) =>
          !system_document?.hide_from_aggregate_dashboard,
      )
    },

    canManageConnector(): boolean {
      return hasWorkspacePermission(
        this.userPermissions,
        this.workspaceId,
        WORKSPACE_PERMISSION.writeConnectorConfig,
      )
    },

    inquiriesBaseUrl(): string {
      if (import.meta.dev) {
        return 'http://localhost:3002'
      }
      const baseUrl = this.activeWorkspace?.system_document
        ?.inquiries_base_url as string | undefined

      return baseUrl?.startsWith('http') ? baseUrl : ''
    },

    chartInfoLabel(): string {
      let info: string
      const { end: endDate, preset, start: startDate } = this.selectedDateRange

      // connector being in claims eval requires batch label
      if (this.activeConnector?.claim_eval) {
        info = `${this.activeConnector?.name} • ${this.activeConnector.batch_label}`
      } else {
        const dateRange = `${startDate} - ${endDate}`
        info = this.activeConnector?.name
          ? `${this.activeConnector?.name} • `
          : ''
        info +=
          preset !== datePresets.custom ? `${preset} (${dateRange})` : dateRange
      }

      return info
    },

    connectors: (state): LocalConnector[] =>
      state.workspaceId
        ? state.userConnectors
            .filter(({ workspace_id }) => workspace_id === state.workspaceId)
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],

    disableClaimsExport(): boolean {
      return Boolean(
        this.activeWorkspace?.system_document?.disable_claims_export,
      )
    },

    featureClaimChain(): boolean {
      const appFeatures = useAppConfiguration()

      return Boolean(
        appFeatures.feature_claim_chain ||
          this.activeWorkspace?.system_document?.feature_claim_chain,
      )
    },

    featurePeriodicRefresh(): boolean {
      const appFeatures = useAppConfiguration()

      return Boolean(
        appFeatures.feature_periodic_refresh ||
          this.activeWorkspace?.system_document?.feature_periodic_refresh,
      )
    },

    getClaimTypeFeatureFlags(): PolicyClaimType[] {
      if (!this.activeWorkspace?.system_document) {
        return []
      }

      const systemDocument = this.activeWorkspace.system_document as Record<
        PolicyClaimType,
        boolean | undefined
      >

      const featureFlags: PolicyClaimType[] = [
        'professional',
        'asc_professional',
        'asc_institutional',
        'outpatient',
        'inpatient',
      ]

      return featureFlags.filter((flag) => systemDocument[flag])
    },

    hasAggregateConnectors(): boolean {
      return this.availableConnectors.length > 0
    },

    hasConnectors(): boolean {
      return this.connectors.length > 0
    },

    hasMany: (state): boolean =>
      state.userWorkspaces && Object.keys(state.userWorkspaces).length > 1,

    isClaimEval(state): boolean {
      if (state.connectorId === ALL_LOB) {
        return this.connectors.every((conn) => conn.claim_eval)
      }
      return !!this.activeConnector?.claim_eval
    },

    isRialticUser: (): boolean => {
      const config = useAppConfig()

      if (config.tenant.hideRialticOnlyViews) {
        return false
      }

      const currentUser = useCurrentUser()

      return Boolean(currentUser.value?.email?.endsWith('@rialtic.io'))
    },

    isRialticAdmin(): boolean {
      if (!this.isRialticUser) {
        return false
      }
      if (!this.userPermissions.length) {
        return false
      }

      return guard(
        'ANY',
        [
          PERMISSIONS.readWorkspacesAll,
          PERMISSIONS.readUsersAll,
          PERMISSIONS.readSystem,
          PERMISSIONS.writeTeamContent,
        ],
        this.userPermissions,
      )
    },

    selectedDateRangeWithTimezone(): SelectedDateRangeWithTimezone {
      const range: [string, string] = this.isClaimEval
        ? [this.sinceInceptionDate, format(new Date(), 'yyyy-MM-dd')]
        : [this.selectedDateRange.start, this.selectedDateRange.end]

      const { timezone } = useWorkspaceTimezone()

      return {
        range,
        timezone: timezone.value,
      }
    },

    rangeTimestamps(): [string, string] {
      const { range, timezone } = this.selectedDateRangeWithTimezone

      return getDateRangeTimestamps(range, timezone, 'iso')
    },

    financialCalculationMethod(): 'none' | 'sabr' | 'client' {
      const isLegacyFinancialCalculation = Boolean(
        !this.activeWorkspace?.impact_calculation_method ||
          this.activeWorkspace?.impact_calculation_method === 'none',
      )
      if (isLegacyFinancialCalculation) {
        return 'none'
      }
      return this.activeWorkspace?.impact_calculation_method ?? 'none'
    },

    muxFilters(): MuxFilters {
      const { range, timezone } = this.selectedDateRangeWithTimezone

      const calculationMethod =
        this.financialCalculationMethod === 'none'
          ? {}
          : { impact_calculation_method: this.financialCalculationMethod }

      const [start_iso, end_iso] = getDateRangeTimestamps(
        range,
        timezone,
        'iso',
      )

      const [start_date, end_date] = getDateRangeTimestamps(
        range,
        timezone,
        'date_tz',
      )

      return {
        connector_id: this.connectorId,
        start_date,
        end_date,
        start_iso,
        end_iso,
        ...calculationMethod,
      }
    },

    muxIsConfigured(state): boolean {
      if (!state.workspaceId || !this.activeWorkspace?.mux_base_url) {
        return false
      }

      if (state.connectorId === ALL_LOB) {
        return true
      }

      return !!(state.connectorId && this.activeConnector)
    },

    selectedDateRange(): DatePickerRange {
      const { getUrlParams } = useUrlParamsState()
      const { $datadog } = useNuxtApp()

      const [start_date] = getUrlParams(DATE_START_KEY)
      const [end_date] = getUrlParams(DATE_END_KEY)
      const [preset] = getUrlParams(PRESET_KEY)

      if (start_date && end_date) {
        return {
          start: start_date,
          end: end_date,
          preset: preset ?? datePresets.custom,
        }
      }

      if (import.meta.dev) {
        try {
          const dateRange = JSON.parse(
            localStorage.getItem(LocalStorageKeys.selectedDateRange) as string,
          )
          if (dateRange) return dateRange
        } catch (error) {
          $datadog.addError(error)
        }
      }

      return getYearToDateRange()
    },

    sinceInceptionDate(): string {
      const minDate =
        this.connectorId === ALL_LOB
          ? min(
              this.connectors
                .filter(
                  (conn) =>
                    !conn.system_document?.hide_from_aggregate_dashboard,
                )
                .map((conn) =>
                  localDate(conn.inception_date ?? sinceInceptionDate),
                ),
            )
          : localDate(
              this.activeConnector?.inception_date ?? sinceInceptionDate,
            )

      return format(minDate, 'yyyy-MM-dd')
    },

    isTobAndRevCodeEnabled(): boolean {
      const institutionalFlags: PolicyClaimType[] = [
        ClaimTypeHiro.ASCInstitutional,
        ClaimTypeHiro.Outpatient,
        ClaimTypeHiro.Inpatient,
      ]

      return this.getClaimTypeFeatureFlags?.some((flag) =>
        institutionalFlags.includes(flag),
      )
    },

    underMaintenance(): boolean {
      return this.activeWorkspace?.system_document?.under_maintenance === true
    },

    userPermissions(): string[] {
      const currentUser = useCurrentUser()

      return currentUser.value?.permissions || []
    },

    /** String of `${workspaceId}:${connectorId}:${start_date}:${end_date}:${claim_eval}` */
    watchKey(state): string {
      return `${state.workspaceId}:${state.connectorId}:${this.muxFilters.start_date}:${this.muxFilters.end_date}:${this.activeConnector?.claim_eval}:${this.financialCalculationMethod}`
    },
  },

  actions: {
    async fetchConnectorData(connectorId: string) {
      const connectorAnalytics = useConnectorAnalytics()

      await this.waitForReady()

      const connector = this.getConnector(connectorId)

      if (!connector?.workspace_id) {
        return
      }

      const refreshEnabled =
        !connector.claim_eval ||
        !connector.system_document?.prevent_realtime_sync

      if (refreshEnabled && this.featurePeriodicRefresh) {
        connectorAnalytics.refreshConnectorAnalytics(
          connector.workspace_id,
          connector.id,
        )
      }

      connectorAnalytics.getAnalyticsAggregate(
        connector.workspace_id,
        connector.id,
      )
    },

    async findAndSetConnector(
      connectorId?: string | null,
      connectorOptions: ConnectorOptions = {},
    ) {
      if (connectorId && connectorOptions.force) {
        this.$patch({
          connectorId,
          workspaceId: '',
        })

        if (import.meta.client) {
          localStorage.removeItem(LocalStorageKeys.workspaceId)

          await nextTick()
        }
      }
      const connId = connectorId || this.connectorId

      const tenantConfig = useAppConfig().tenant
      const connector = this.userConnectors
        .filter((conn) => {
          if (tenantConfig.singleTenantWorkspaceId) {
            return conn.workspace_id === tenantConfig.singleTenantWorkspaceId
          }
          return true
        })
        .find(({ id }) => id === connId)

      if (connector?.workspace_id) {
        this.setActiveWorkspace(connector.workspace_id)
        this.setActiveConnector(connector.id, connectorOptions)
        return connector
      }

      await this.getUser(connectorId || undefined)

      if (!this.userConnectors.length) {
        this.setActiveConnector('', connectorOptions)
        return null
      }

      const userConnector = this.userConnectors.find(({ id }) => id === connId)

      const setActiveConnectorId =
        userConnector?.id || this.connectors[0]?.id || ''

      this.setActiveConnector(setActiveConnectorId, connectorOptions)

      return this.activeConnector
    },

    getConnector(connectorId: string): LocalConnector | undefined {
      return this.userConnectors.find(({ id }) => connectorId === id)
    },

    async getUser(connectorId?: string) {
      const { $auth0, $datadog } = useNuxtApp()
      const { isAuthenticated } = $auth0()

      if (!isAuthenticated.value) {
        this.$reset()
        return null
      }

      try {
        const client = $apiFetcher()
        const userData = (await client(
          '/api/auth-user',
        )) as Api.AuthUserResponse

        const { data } = userData

        if (!data) {
          throw new Error('No user data returned')
        }

        const { user } = $auth0()

        this.$patch({
          globalRoles: data.globalRoles,
          connectorRoles: data.connectorRoles,
          workspaceRoles: data.workspaceRoles,
          userConnectors: data.connectors as any,
          userWorkspaces: data.workspaces,
          user: {
            id: data.id ?? user.value?.email ?? '',
            email: data.email ?? user.value?.email ?? '',
            first_name: data.first_name ?? user.value?.given_name ?? '',
            last_name: data.last_name ?? user.value?.family_name ?? '',
          },
        })

        if (connectorId) {
          this.setActiveConnector(connectorId)

          const connector = this.getConnector(connectorId)
          if (connector?.workspace_id)
            this.setActiveWorkspace(connector.workspace_id)
          return data
        }
        const wId =
          this.workspaceId || localStorage.getItem(LocalStorageKeys.workspaceId)

        if (wId && data.workspaces[wId]) {
          this.setActiveWorkspace(wId)
        } else {
          this.setActiveWorkspace(Object.keys(data.workspaces)[0])
        }

        for (const connector of this.availableConnectors) {
          this.selectedConnectors.add(connector.id)
        }

        return data
      } catch (error) {
        $datadog.addError(error)
        return null
      }
    },

    setActiveConnector(id: string, options: ConnectorOptions = {}): string {
      const { deleteUrlParam, setUrlParams } = useUrlParamsState()
      if (!id) {
        this.connectorId = ''
        deleteUrlParam('lob')

        return id
      }
      if (id === this.connectorId) return id

      if (this.connectorId && this.connectorId !== ALL_LOB) {
        this.previousConnectorId = this.connectorId
      }

      const { $datadog } = useNuxtApp()
      $datadog.setUserProperty('connector_id', id)
      $datadog.setGlobalContextProperty('connector_id', id)

      this.connectorId = id

      if (id === ALL_LOB) {
        return id
      }

      this.syncPaApi()
      this.fetchConnectorData(id)

      if (options.middleware) {
        return id
      }

      setUrlParams('lob', id)

      return id
    },

    async setActiveWorkspace(id: string): Promise<string> {
      const { $datadog } = useNuxtApp()

      const tenantConfig = useAppConfig().tenant
      if (tenantConfig.singleTenantWorkspaceId) {
        if (this.workspaceId !== tenantConfig.singleTenantWorkspaceId) {
          this.workspaceId = tenantConfig.singleTenantWorkspaceId
        }
        $datadog.setUserProperty(
          'workspace_id',
          tenantConfig.singleTenantWorkspaceId,
        )
        $datadog.setGlobalContextProperty(
          'workspace_id',
          tenantConfig.singleTenantWorkspaceId,
        )
        return tenantConfig.singleTenantWorkspaceId
      }
      if (id && id === this.workspaceId) return id

      if (id) {
        localStorage.setItem(LocalStorageKeys.workspaceId, id)
      }

      $datadog.setUserProperty('workspace_id', id)
      $datadog.setGlobalContextProperty('workspace_id', id)

      this.workspaceId = id

      return id
    },

    setSelectedDateRange(dateRange: WorkersApiLegacy.DateRange) {
      localStorage.setItem(
        LocalStorageKeys.selectedDateRange,
        JSON.stringify(dateRange),
      )

      const { patchUrlParams } = useUrlParamsState()
      patchUrlParams({
        [DATE_START_KEY]: dateRange.start,
        [DATE_END_KEY]: dateRange.end,
        [PRESET_KEY]: dateRange.preset ?? '',
      })

      this.syncPaApi()
    },

    async switchWorkspace(workspaceId: string) {
      const tenantConfig = useAppConfig().tenant

      if (tenantConfig.singleTenantWorkspaceId) {
        return tenantConfig.singleTenantWorkspaceId
      }

      const { deleteUrlParam } = useUrlParamsState()

      deleteUrlParam('lob')
      this.setActiveConnector('')
      this.selectedConnectors.clear()
      this.setActiveWorkspace(workspaceId)

      if (this.connectors.length) {
        this.setActiveConnector(this.connectors[0].id)

        for (const connector of this.availableConnectors) {
          this.selectedConnectors.add(connector.id)
        }
      }

      return workspaceId
    },

    async syncPaApi({ flush } = { flush: false }) {
      if (!this.connectorId || this.connectorId === ALL_LOB) {
        return
      }
      const { $auth0, $datadog } = useNuxtApp()
      const { getAccessTokenSilently, isAuthenticated } = $auth0()

      try {
        const token = await getAccessTokenSilently()

        const client = useRpcClient(token)

        if (!isAuthenticated.value) {
          this.$reset()
          return null
        }

        await client.event.connector[':connectorId'].sync.$get({
          param: {
            connectorId: this.connectorId,
          },
          query: {
            start_date: this.muxFilters.start_date,
            end_date: this.muxFilters.end_date,
            ...(flush && { flush: 'true' }),
          },
        })
      } catch (error) {
        $datadog.addError(error)
      }
    },

    waitForReady(timeout = 3000): Promise<boolean> {
      return new Promise((resolve, reject) => {
        if (
          hasConnectors(this.workspaceId, this.connectorId, this.connectors)
        ) {
          return resolve(true)
        }

        const activeTimeout = setTimeout(async () => {
          if (
            hasConnectors(this.workspaceId, this.connectorId, this.connectors)
          ) {
            return resolve(true)
          }
          try {
            await this.getUser()
            return resolve(true)
          } catch (error) {
            return reject(error)
          }
        }, timeout)

        const sub = this.$subscribe((_e, state) => {
          if (
            hasConnectors(state.workspaceId, state.connectorId, this.connectors)
          ) {
            clearTimeout(activeTimeout)
            sub()
            return resolve(true)
          }
        })
      })
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useWorkspace, import.meta.hot))
